import zoomSdk from '@zoom/appssdk';
import type { ZoomMessage, MeetingContext, BLSState } from '../types';

/**
 * Zoom API Wrapper - Handles all Zoom Apps SDK interactions
 *
 * Features:
 * - SDK initialization and configuration
 * - Meeting context retrieval
 * - Message broadcasting between participants
 * - Role detection (host vs participant)
 */

export class ZoomAPI {
  private isInitialized: boolean = false;
  private meetingContext: MeetingContext | null = null;
  private messageHandlers: Array<(message: ZoomMessage) => void> = [];
  private authorizedHandlers: Array<(data: any) => void> = [];

  /**
   * Initialize the Zoom Apps SDK
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      console.log('Zoom SDK already initialized');
      return;
    }

    try {
      // Request only essential capabilities
      const configResponse = await zoomSdk.config({
        capabilities: [
          'getMeetingContext',
          'getUserContext',
          'getRunningContext',
          'getMeetingParticipants', // For checking if therapist is alone
          'sendAppInvitation',
          'sendMessage', // For broadcasting messages between different participants
          'onMessage', // For receiving messages (from sendMessage AND postMessage)
          'postMessage', // For sending messages between instances of same user (sidebar ↔ immersive)
          'runRenderingContext', // For immersive fullscreen mode (hides all videos)
          'closeRenderingContext', // For returning to sidebar
          'sendAppToBackground', // For hiding the sidebar panel
          'bringAppToFront', // For showing the sidebar panel again
          'openUrl', // For opening external URLs (OAuth flows)
          'authorize', // For Zoom In-Client OAuth
          'onAuthorized', // For receiving OAuth authorization codes
        ],
        version: '0.16',
      });

      console.log('[ZoomAPI] ========== SDK CONFIGURATION ==========');
      console.log('[ZoomAPI] Full config response:', JSON.stringify(configResponse, null, 2));
      console.log('[ZoomAPI] Checking if authorize capability was granted...');

      // Check if authorize capability is in the approved list
      const configAny = configResponse as any;
      if (configAny.capabilities) {
        const hasAuthorize = configAny.capabilities.includes('authorize');
        const hasOnAuthorized = configAny.capabilities.includes('onAuthorized');
        console.log('[ZoomAPI] ✓ authorize capability granted:', hasAuthorize);
        console.log('[ZoomAPI] ✓ onAuthorized capability granted:', hasOnAuthorized);

        if (!hasAuthorize) {
          console.error('[ZoomAPI] ❌ WARNING: authorize capability NOT granted!');
          console.error('[ZoomAPI] This means in-client OAuth will not work.');
          console.error('[ZoomAPI] Check your Zoom Marketplace app configuration.');
        }
      }
      console.log('[ZoomAPI] ============================================');

      // Get user context (includes role information)
      const userContext = await zoomSdk.getUserContext();
      this.meetingContext = {
        role: userContext.role,
        participantId: userContext.participantId,
        meetingId: userContext.participantUUID || '',
      };
      console.log('User context:', userContext);
      console.log('Parsed meeting context:', this.meetingContext);

      // Set up message listener for both sendMessage and postMessage
      // onMessage receives messages from both:
      // - sendMessage: messages from other participants
      // - postMessage: messages from other instances of same user (sidebar ↔ immersive)
      zoomSdk.onMessage((data: any) => {
        console.log('[onMessage] Raw message received:', data);
        this.handleIncomingMessage(data);
      });

      // Set up onAuthorized listener during initialization (CRITICAL!)
      // This must be done BEFORE calling authorize(), or the event won't fire
      console.log('[ZoomAPI] Setting up global onAuthorized listener...');
      zoomSdk.onAuthorized((data: any) => {
        console.log('[ZoomAPI] 🎉 GLOBAL onAuthorized event fired!', data);
        // Notify all registered handlers
        this.authorizedHandlers.forEach(handler => {
          try {
            handler(data);
          } catch (error) {
            console.error('[ZoomAPI] Error in authorized handler:', error);
          }
        });
      });

      this.isInitialized = true;
      console.log('Zoom SDK initialized successfully. Role:', this.getUserRole());
    } catch (error) {
      console.error('Failed to initialize Zoom SDK:', error);
      console.error('Error details:', error);
      throw error;
    }
  }

  /**
   * Get the current meeting context
   */
  getMeetingContext(): MeetingContext | null {
    return this.meetingContext;
  }

  /**
   * Check if current user is host or co-host
   */
  isHost(): boolean {
    if (!this.meetingContext) return false;
    const role = this.meetingContext.role?.toLowerCase();
    return role === 'host' || role === 'cohost';
  }

  /**
   * Get user role
   */
  getUserRole(): 'host' | 'participant' {
    return this.isHost() ? 'host' : 'participant';
  }

  /**
   * Get the count of participants in the meeting
   * Returns null if API call fails
   */
  async getMeetingParticipantCount(): Promise<number | null> {
    if (!this.isInitialized) {
      console.warn('Zoom SDK not initialized. Cannot get participant count.');
      return null;
    }

    try {
      const participants = await zoomSdk.getMeetingParticipants();
      console.log('[ZoomAPI] Meeting participants:', participants);

      // The API returns { participants: [...] }
      if (participants && participants.participants) {
        return participants.participants.length;
      }
      return null;
    } catch (error) {
      console.error('[ZoomAPI] Failed to get meeting participants:', error);
      return null;
    }
  }

  /**
   * Send a message to all participants
   */
  async broadcastMessage(message: ZoomMessage): Promise<void> {
    if (!this.isInitialized) {
      console.warn('Zoom SDK not initialized. Cannot send message.');
      return;
    }

    try {
      // Use sendMessage to broadcast to all app instances across participants
      // Payload must be JSON-stringifiable and < 1KB
      await zoomSdk.sendMessage({
        payload: message as any,
      });
    } catch (error) {
      console.error('Failed to broadcast message:', error, message);
      throw error;
    }
  }

  /**
   * Send message to other instances of the same user (sidebar ↔ immersive)
   */
  async sendToOtherInstances(message: ZoomMessage): Promise<void> {
    if (!this.isInitialized) {
      console.warn('Zoom SDK not initialized. Cannot send postMessage.');
      return;
    }

    try {
      // Use postMessage to send to other rendering contexts of same user
      await zoomSdk.postMessage({
        payload: message as any,
      });
      console.log('Message sent via postMessage to other instances:', message);
    } catch (error) {
      console.error('Failed to send postMessage:', error, message);
      // Don't throw - postMessage might fail if no other instance exists
    }
  }

  /**
   * Start BLS session (host only)
   */
  async startBLS(state: Partial<BLSState>): Promise<void> {
    const message: ZoomMessage = {
      action: 'start_bls',
      params: state,
    };
    await this.broadcastMessage(message);
  }

  /**
   * Stop BLS session (host only)
   */
  async stopBLS(): Promise<void> {
    const message: ZoomMessage = {
      action: 'stop_bls',
    };
    await this.broadcastMessage(message);
  }

  /**
   * Update BLS state (host only)
   */
  async updateState(state: Partial<BLSState>): Promise<void> {
    const message: ZoomMessage = {
      action: 'update_state',
      params: state,
    };
    await this.broadcastMessage(message);
  }

  /**
   * Notify that client stopped BLS (participant only)
   */
  async notifyClientStopped(): Promise<void> {
    const message: ZoomMessage = {
      action: 'client_stopped',
    };
    await this.broadcastMessage(message);
  }

  /**
   * Send app invitation to all participants
   * This triggers the notification for participants to join the app
   */
  async inviteAllParticipants(): Promise<void> {
    if (!this.isInitialized) {
      console.warn('Zoom SDK not initialized. Cannot send invitation.');
      return;
    }

    try {
      console.log('Attempting to send app invitation to all participants...');
      const result = await zoomSdk.sendAppInvitationToAllParticipants();
      console.log('App invitation sent successfully:', result);
    } catch (error) {
      console.error('Failed to send app invitation:', error);
      console.error('Error details:', JSON.stringify(error, null, 2));
      // Don't throw - continue with broadcast even if invitation fails
    }
  }

  /**
   * Enter immersive fullscreen mode (hides all videos, like whiteboard)
   * Takes over the entire meeting window for BLS
   */
  async expandToFullscreen(): Promise<void> {
    if (!this.isInitialized) {
      console.warn('Zoom SDK not initialized. Cannot enter immersive mode.');
      return;
    }

    try {
      console.log('Entering immersive fullscreen mode (hiding all videos)...');

      // Enter immersive rendering context - this moves the app to the main window
      // and hides ALL participant videos (like whiteboard)
      await zoomSdk.runRenderingContext({ view: 'immersive', defaultCutout: "rectangle" });

      console.log('App entered immersive mode - all videos hidden');
    } catch (error) {
      console.error('Failed to enter immersive mode:', error);
      console.error('Error details:', error);
      // Don't throw - BLS can still work in sidebar if immersive mode fails
    }
  }

  /**
   * Enter immersive fullscreen mode (hides all videos, like whiteboard)
   * Takes over the entire meeting window for BLS
   */
  async sendAppToBackground(): Promise<void> {
    if (!this.isInitialized) {
      console.warn('Zoom SDK not initialized. Cannot enter immersive mode.');
      return;
    }

    try {
      console.log('Sending app to background');
      await zoomSdk.sendAppToBackground();
      console.log('App set to background');
    } catch (error) {
      console.error('Failed to send app to background:', error);
    }
  }

  /**
   * Exit immersive mode and return to sidebar
   */
  async collapseToSidebar(): Promise<void> {
    if (!this.isInitialized) {
      console.warn('Zoom SDK not initialized. Cannot exit immersive mode.');
      return;
    }

    try {
      console.log('Exiting immersive mode...');
      await zoomSdk.closeRenderingContext();
      console.log('Closed immersive rendering context');
    } catch (error) {
      console.error('Failed to exit immersive mode:', error);
      // Don't throw - not critical if closing fails
    }
  }

  /**
   * Bring app panel to front (show sidebar)
   */
  async bringAppToFront(): Promise<void> {
    if (!this.isInitialized) {
      console.warn('Zoom SDK not initialized. Cannot bring app to front.');
      return;
    }

    try {
      console.log('Bringing app to front...');
      await zoomSdk.bringAppToFront();
      console.log('App brought to front - sidebar visible again');
    } catch (error) {
      console.error('Failed to bring app to front:', error);
    }
  }

  /**
   * Open a URL in a new browser window (bypasses Zoom iframe restrictions)
   * Useful for OAuth flows and external links
   */
  async openUrl(url: string): Promise<void> {
    if (!this.isInitialized) {
      console.warn('Zoom SDK not initialized. Cannot open URL.');
      return;
    }

    try {
      console.log('[ZoomAPI] Calling openUrl with:', url);
      const result = await zoomSdk.openUrl({ url });
      console.log('[ZoomAPI] openUrl result:', result);
      console.log('[ZoomAPI] URL opened successfully');
    } catch (error) {
      console.error('[ZoomAPI] Failed to open URL:', error);
      console.error('[ZoomAPI] Error details:', JSON.stringify(error, null, 2));
      throw error;
    }
  }

  /**
   * Authorize with Zoom OAuth (In-Client OAuth)
   * Uses PKCE flow for security
   * @param codeChallenge The PKCE code challenge
   * @param state Random state for CSRF protection
   * @returns Object with authorization code and redirect URI
   */
  async authorizeWithZoom(codeChallenge: string, state: string): Promise<{ code: string; redirectUri: string }> {
    if (!this.isInitialized) {
      console.warn('[ZoomAPI] Zoom SDK not initialized. Cannot authorize.');
      throw new Error('Zoom SDK not initialized');
    }

    try {
      console.log('[ZoomAPI] Starting Zoom In-Client OAuth...');
      console.log('[ZoomAPI] State:', state);
      console.log('[ZoomAPI] Code Challenge:', codeChallenge);

      // Create a promise that resolves when onAuthorized event fires
      const authPromise = new Promise<{ code: string; redirectUri: string }>((resolve, reject) => {
        // Set up one-time listener for authorized event
        const handleAuthorized = (data: any) => {
          console.log('[ZoomAPI] ✅ onAuthorized event received in handler!');
          console.log('[ZoomAPI] Authorization data:', JSON.stringify(data, null, 2));

          // Remove this handler from the array (one-time use)
          const index = this.authorizedHandlers.indexOf(handleAuthorized);
          if (index > -1) {
            this.authorizedHandlers.splice(index, 1);
            console.log('[ZoomAPI] Handler removed from authorizedHandlers array');
          }

          // Note: Zoom In-Client OAuth does NOT return the state parameter
          // Zoom validates it internally and only returns: code, redirectUri, result, timestamp
          // This is safe because in-client OAuth is already protected by Zoom SDK context
          console.log('[ZoomAPI] State validation skipped (Zoom validates internally)');

          // Check for error
          if (data.error) {
            console.error('[ZoomAPI] ❌ OAuth error:', data.error, data.error_description);
            reject(new Error(`OAuth error: ${data.error_description || data.error}`));
            return;
          }

          // Check result flag
          if (data.result === false) {
            console.error('[ZoomAPI] ❌ Authorization failed - user denied or error occurred');
            reject(new Error('Authorization failed'));
            return;
          }

          // Success - return authorization code AND redirect URI
          if (data.code && data.redirectUri) {
            console.log('[ZoomAPI] ✅ Authorization code received:', data.code.substring(0, 20) + '...');
            console.log('[ZoomAPI] ✅ Redirect URI:', data.redirectUri);
            resolve({
              code: data.code,
              redirectUri: data.redirectUri // Use as-is (already decoded)
            });
          } else {
            console.error('[ZoomAPI] ❌ No authorization code or redirect URI in response');
            reject(new Error('No authorization code or redirect URI received'));
          }
        };

        // Register handler in the global handlers array
        // The global listener (set up in initialize()) will call this
        console.log('[ZoomAPI] Registering handler in authorizedHandlers array...');
        this.authorizedHandlers.push(handleAuthorized);
        console.log('[ZoomAPI] Handler registered. Total handlers:', this.authorizedHandlers.length);

        // Timeout after 2 minutes (reduced from 5 for faster debugging)
        setTimeout(() => {
          // Remove handler on timeout
          const index = this.authorizedHandlers.indexOf(handleAuthorized);
          if (index > -1) {
            this.authorizedHandlers.splice(index, 1);
          }
          console.error('[ZoomAPI] ❌ OAuth authorization timeout - no response after 2 minutes');
          reject(new Error('OAuth authorization timeout'));
        }, 2 * 60 * 1000);
      });

      // Trigger authorization flow
      // Note: The redirect URI comes from Zoom Marketplace OAuth configuration
      // Scopes are automatically included based on Zoom Marketplace app configuration
      console.log('[ZoomAPI] Calling zoomSdk.authorize()...');
      const authorizeResult = await zoomSdk.authorize({
        state,
        codeChallenge
      });
      console.log('[ZoomAPI] authorize() call completed, result:', authorizeResult);

      console.log('[ZoomAPI] Waiting for user to approve in dialog...');

      // Wait for authorization to complete
      const result = await authPromise;
      console.log('[ZoomAPI] ✅ Authorization complete!');
      return result;
    } catch (error) {
      console.error('[ZoomAPI] ❌ Failed to authorize with Zoom:', error);
      console.error('[ZoomAPI] Error details:', error);
      throw error;
    }
  }

  /**
   * Get Zoom App Context (contains encrypted user identity)
   * This context must be sent to the server for decryption
   * @returns Encrypted app context string
   */
  async getAppContext(): Promise<string> {
    if (!this.isInitialized) {
      console.warn('[ZoomAPI] Zoom SDK not initialized. Cannot get app context.');
      throw new Error('Zoom SDK not initialized');
    }

    try {
      console.log('[ZoomAPI] Getting app context...');
      const context = await zoomSdk.getAppContext();
      console.log('[ZoomAPI] App context retrieved (encrypted)');

      // The context is an encrypted string that must be decrypted server-side
      // It contains the user_id and other identity information
      return JSON.stringify(context);
    } catch (error) {
      console.error('[ZoomAPI] Failed to get app context:', error);
      throw error;
    }
  }

  /**
   * Handle incoming messages
   */
  private handleIncomingMessage(data: any): void {
    try {
      let message: ZoomMessage;

      // Parse message data from sendMessage/onMessage format
      if (data.payload) {
        // onMessage sends data in { payload: {...}, sender: {...} } format
        // Check if payload is a string that needs parsing
        if (typeof data.payload === 'string') {
          const parsed = JSON.parse(data.payload);
          // If the parsed result has a payload property, unwrap it
          message = parsed.payload || parsed;
        } else {
          message = data.payload as ZoomMessage;
        }
      } else if (typeof data === 'string') {
        message = JSON.parse(data);
      } else if (data.data && typeof data.data === 'string') {
        message = JSON.parse(data.data);
      } else {
        message = data as ZoomMessage;
      }

      console.log('Parsed message:', message);

      // Notify all registered handlers
      this.messageHandlers.forEach(handler => {
        try {
          handler(message);
        } catch (error) {
          console.error('Error in message handler:', error);
        }
      });
    } catch (error) {
      console.error('Failed to parse incoming message:', error);
    }
  }

  /**
   * Register a message handler
   */
onMessage(handler: (message: ZoomMessage) => void): () => void {
    this.messageHandlers.push(handler);

    // Return unsubscribe function
    return () => {
      const index = this.messageHandlers.indexOf(handler);
      if (index > -1) {
        this.messageHandlers.splice(index, 1);
      }
    };
  }

  /**
   * Check if SDK is initialized
   */
  isReady(): boolean {
    return this.isInitialized;
  }

  /**
   * Get running context (for debugging)
   */
  async getRunningContext(): Promise<any> {
    try {
      return await zoomSdk.getRunningContext();
    } catch (error) {
      console.error('Failed to get running context:', error);
      return null;
    }
  }

  /**
   * Clean up resources
   */
  dispose(): void {
    this.messageHandlers = [];
    this.isInitialized = false;
    this.meetingContext = null;
  }
}

// Create singleton instance
let zoomAPIInstance: ZoomAPI | null = null;

/**
 * Get the singleton Zoom API instance
 */
export function getZoomAPI(): ZoomAPI {
  if (!zoomAPIInstance) {
    zoomAPIInstance = new ZoomAPI();
  }
  return zoomAPIInstance;
}

/**
 * Mock Zoom API for development/testing
 * Uses BroadcastChannel to simulate message passing between tabs
 */
export class MockZoomAPI extends ZoomAPI {
  private mockRole: 'host' | 'participant' = 'host';
  private broadcastChannel: BroadcastChannel | null = null;
  private connectedParticipants: Set<string> = new Set();
  private participantId: string = '';

  async initialize(): Promise<void> {
    console.log('Mock Zoom SDK initialized with role:', this.mockRole);
    this.participantId = `mock-${this.mockRole}-${Date.now()}`;
    // @ts-ignore
    this.isInitialized = true;
    // @ts-ignore
    this.meetingContext = {
      role: this.mockRole,
      participantId: this.participantId,
      meetingId: 'mock-meeting-id',
    };

    // Set up BroadcastChannel for cross-tab communication
    try {
      this.broadcastChannel = new BroadcastChannel('emdr-sync-dev');
      this.broadcastChannel.onmessage = (event) => {
        const data = event.data;

        // Handle participant presence announcements
        if (data._mockPresence === 'join') {
          this.connectedParticipants.add(data._participantId);
          console.log('[MockZoomAPI] Participant joined:', data._participantId, 'Total:', this.connectedParticipants.size + 1);
          // Respond with our presence so they know we're here
          this.broadcastChannel?.postMessage({ _mockPresence: 'here', _participantId: this.participantId });
        } else if (data._mockPresence === 'here') {
          this.connectedParticipants.add(data._participantId);
          console.log('[MockZoomAPI] Participant already here:', data._participantId, 'Total:', this.connectedParticipants.size + 1);
        } else if (data._mockPresence === 'leave') {
          this.connectedParticipants.delete(data._participantId);
          console.log('[MockZoomAPI] Participant left:', data._participantId, 'Total:', this.connectedParticipants.size + 1);
        } else {
          // Regular message
          console.log('Mock received broadcast:', data);
          // @ts-ignore
          this.handleIncomingMessage(data);
        }
      };
      console.log('BroadcastChannel initialized for cross-tab messaging');

      // Announce our presence to other tabs
      this.broadcastChannel.postMessage({ _mockPresence: 'join', _participantId: this.participantId });
    } catch (error) {
      console.warn('BroadcastChannel not available:', error);
    }
  }

  setMockRole(role: 'host' | 'participant'): void {
    this.mockRole = role;
    // @ts-ignore
    if (this.meetingContext) {
      // @ts-ignore
      this.meetingContext.role = role;
    }
  }

  async broadcastMessage(message: ZoomMessage): Promise<void> {
    console.log('Mock broadcast to all tabs:', message);

    // Broadcast to other tabs via BroadcastChannel
    if (this.broadcastChannel) {
this.broadcastChannel.postMessage(message);
    }

    // Also trigger on current tab after a small delay
    setTimeout(() => {
      // @ts-ignore
      this.handleIncomingMessage(message);
    }, 50);
  }

  async sendToOtherInstances(message: ZoomMessage): Promise<void> {
    console.log('Mock sendToOtherInstances (same-user only):', message);
    // In mock mode, we simulate same-user communication by NOT using BroadcastChannel
    // BroadcastChannel simulates cross-participant communication (sendMessage)
    // For postMessage (same-user), we only trigger on current tab
    // This prevents therapist messages from reaching client tabs

    // No-op in mock mode - messages stay within same tab
    // In real Zoom, postMessage would go to other rendering contexts of same user
  }

  async inviteAllParticipants(): Promise<void> {
    console.log('Mock: App invitation sent to all participants');
    // No-op in mock mode - user should manually open second tab
  }

  async getMeetingParticipantCount(): Promise<number | null> {
    console.log('[MockZoomAPI] Mock participant count - returning 1 (therapist alone)');
    // In dev mode, always return 1 to simulate therapist testing alone
    // User can test collaborative mode by opening multiple tabs
    return 1;
  }

  async getRunningContext(): Promise<any> {
    // Return mock running context based on role
    return {
      context: this.mockRole === 'host' ? 'inMeeting' : 'inMeeting',
    };
  }

  async authorizeWithZoom(codeChallenge: string, state: string): Promise<{ code: string; redirectUri: string }> {
    console.log('[MockZoomAPI] Mock Zoom OAuth - returning fake authorization code');
    console.log('[MockZoomAPI] State:', state);
    console.log('[MockZoomAPI] Code Challenge:', codeChallenge);

    // Simulate async OAuth flow
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Return mock authorization code and redirect URI
    return {
      code: 'mock_auth_code_' + Date.now(),
      redirectUri: 'http://localhost:3000'
    };
  }

  async getAppContext(): Promise<string> {
    console.log('[MockZoomAPI] Mock app context - returning fake encrypted context');

    // Return mock encrypted context
    return JSON.stringify({
      typ: 'jwt',
      payload: 'mock_encrypted_user_context'
    });
  }

  dispose(): void {
    super.dispose();
    if (this.broadcastChannel) {
      this.broadcastChannel.close();
      this.broadcastChannel = null;
    }
  }
}
